import { useCallback, useContext, useEffect } from 'react';

import { sendGTMEvent } from 'helpers/google-tag-manager';
import { useTranslation } from 'react-i18next';

import { Text } from '@olist/united';

import { Container } from '../../components/layout';
import { AuthContext } from '../../contexts/auth';
import useFirstSteps from '../../hooks/use-first-steps';
import useMixPanel from '../../hooks/use-mixpanel';
import { DASHBOARD_URL } from '../../utils/urls';
import { HIDE_PLAN_CHOICE_BUTTON_REGEX, mixPanelEventTitles } from './constants';
import OnboardingSteps from './onboarding-steps/onboarding-steps';

const OnboardingPage = () => {
  const { t } = useTranslation();

  const { isGtmLoaded, userInfo } = useContext(AuthContext);
  const firstStepsProps = useFirstSteps();
  const displayName = userInfo?.displayName;
  const { track, trackLink } = useMixPanel();

  useEffect(() => {
    if (userInfo?.email) {
      sendGTMEvent('page_view', { user_id: userInfo.email });
    }
  }, [userInfo.email]);

  useEffect(() => {
    if (firstStepsProps.isAllStepsCompleted) {
      window.location.assign(DASHBOARD_URL);
    }
  }, [firstStepsProps.isAllStepsCompleted]);

  useEffect(() => {
    track(mixPanelEventTitles.ONBOARDING);
  }, [userInfo.isAdmin, track]);

  const onCardClick = (stepTitle) => {
    track(mixPanelEventTitles.ONBOARDING_STEPS_CARDS[stepTitle]);
  };

  const onChangeStepContent = useCallback(
    (stepName) => {
      trackLink(`#${stepName} a`, mixPanelEventTitles.ONBOARDING_STEPS_LINKS[stepName]);
    },
    [trackLink]
  );

  const hasUserInfo = typeof userInfo.planType !== 'undefined';
  const hasMidPlan = String(userInfo?.planType).toLowerCase() === 'mid';

  const { subscription } = firstStepsProps;
  const hasSubscription = subscription && typeof subscription.representative_email === 'string';
  const hasRepresentativeEmail = hasSubscription && subscription.representative_email !== '';

  const shouldHideSubscriptionStep = subscription?.plan?.slug.match(
    new RegExp(HIDE_PLAN_CHOICE_BUTTON_REGEX, 'gi')
  );
  const shouldShowGenericChosePlan = hasUserInfo && !hasMidPlan;
  const shouldShowChoosePlanFromInvite =
    hasUserInfo && subscription?.plan?.active && hasRepresentativeEmail;

  const onboardingStepsProps = {
    isGtmLoaded,
    shouldShowGenericChosePlan,
    shouldShowChoosePlanFromInvite,
    onCardClick,
    onChangeStepContent,
    shouldHideSubscriptionStep,
  };

  return (
    <>
      <Container>
        <Text.H3 mt={2} mb={4} fontWeight="semibold">
          {t(`hello {{displayName}}`, { displayName })}
        </Text.H3>
        <OnboardingSteps {...firstStepsProps} {...onboardingStepsProps} />
      </Container>
    </>
  );
};

export default OnboardingPage;
